import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCookieConsent, cookieCategoriesInfo } from '../hooks/useCookieConsent';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const {
    showBanner,
    showPreferenceModal,
    consentState,
    acceptAll,
    rejectAll,
    savePreferences,
    updateCookiePreference,
    closePreferenceModal,
  } = useCookieConsent();

  const [activeTab, setActiveTab] = useState<'overview' | 'details'>('overview');

  if (!showBanner && !showPreferenceModal) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {showBanner && !showPreferenceModal && (
          <motion.div
            initial={{ y: 16, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="cookie-consent-banner fixed z-[9999]"
            role="dialog"
            aria-live="polite"
            aria-label={t('layout.cookieBanner.message')}
          >
            <div className="cookie-consent-banner__card">
              <p className="cookie-consent-banner__text">
                {t('layout.cookieBanner.message')}{' '}
                <a href="/privacy-policy" className="cookie-consent-banner__link">
                  {t('layout.cookieBanner.privacyPolicy')}
                </a>
                .
              </p>

              <div className="cookie-consent-banner__actions">
                <button type="button" onClick={acceptAll} className="cookie-consent-banner__accept">
                  {t('layout.cookieBanner.accept')}
                </button>
                <button type="button" onClick={rejectAll} className="cookie-consent-banner__opt-out">
                  {t('layout.cookieBanner.optOut')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPreferenceModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePreferenceModal}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Privacy Preferences</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Manage your cookie preferences</p>
                      </div>
                    </div>
                    <button
                      onClick={closePreferenceModal}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      aria-label={t('layout.ariaCloseMenu')}
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`flex-1 min-h-[44px] px-4 py-2 rounded-lg font-medium transition-all touch-manipulation ${
                        activeTab === 'overview'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('details')}
                      className={`flex-1 min-h-[44px] px-4 py-2 rounded-lg font-medium transition-all touch-manipulation ${
                        activeTab === 'details'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      Cookie Details
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      {cookieCategoriesInfo.map((category) => (
                        <div key={category.id} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900 dark:text-white mb-1">{category.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{category.description}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">
                                {category.cookies.length} cookie(s) in this category
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              {category.required ? (
                                <div className="px-3 py-1 bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-semibold rounded-full">
                                  Always Active
                                </div>
                              ) : (
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={consentState[category.id]}
                                    onChange={(e) => updateCookiePreference(category.id, e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-300 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" />
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <div className="space-y-6">
                      {cookieCategoriesInfo.map((category) => (
                        <div key={category.id}>
                          <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            {category.name}
                            {category.required && (
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                                Required
                              </span>
                            )}
                          </h3>
                          <div className="space-y-3">
                            {category.cookies.map((cookie, idx) => (
                              <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                <div className="flex items-start justify-between gap-4 mb-2">
                                  <span className="font-mono text-sm font-semibold text-purple-600 dark:text-purple-400">
                                    {cookie.name}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-500">{cookie.duration}</span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{cookie.purpose}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-500">Provider: {cookie.provider}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <button
                      onClick={rejectAll}
                      className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={savePreferences}
                      className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold hover:opacity-90 transition-all"
                    >
                      Save Preferences
                    </button>
                    <button
                      onClick={acceptAll}
                      className="px-6 py-3 text-white rounded-xl font-semibold hover:opacity-90 transition-all"
                      style={{ background: 'var(--brand-cta-bg)' }}
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
